import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { StepItem, StepList } from './step-list';
import { useCreateMatter } from '../../../api/mutations/useCreateMatter';
import { Matter } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';

export const SubsetCreationMatterSelection = ({
  matters,
  isOpen,
  setIsOpen,
}: {
  matters: Matter[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { mutateAsync: createMatter } = useCreateMatter();
  const handleCreateMatter = async () => {
    await createMatter(undefined, {
      onSuccess: (data) => {
        navigate(`/matters/new/${data.id}`);
      },
    });
  };

  const matterCards = matters && matters.length > 0 && (
    <>
      <p className="mb-2 text-xs text-gray-700">
        It looks like you are already a member of some Matters! Choose one to create a Subset in:
      </p>
      <div className="mb-4 max-h-60 overflow-y-auto rounded border bg-gray-50 p-4">
        {matters.map((matter) => (
          <button
            key={matter.id}
            type="button"
            data-testid={`onboarding-case-creation-matter-card-${matter.id}`}
            onClick={() =>
              navigate(`/matters/${matter.id}/subsets/new`, { state: { fromChecklistSubsetCreation: true } })
            }
            className="group mb-2 flex w-full cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2 text-left transition-all hover:border-purple-300 hover:bg-purple-50 hover:shadow-md"
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-purple-100">
              <Icon className="lucide--folder h-5 w-5 text-purple-700" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">{matter.name}</h4>
              <p className="line-clamp-1 text-xs text-gray-600">{matter.description}</p>
            </div>
            <Icon className="lucide--arrow-right h-4 w-4 text-purple-500 opacity-0 transition-opacity group-hover:opacity-100" />
          </button>
        ))}
      </div>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl" data-testid="onboarding-case-creation-matter-selection-modal">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--group" />
            Create a Subset
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="mb-4 text-sm text-gray-600">
          Subsets help you focus your analysis by only showing you facts from specific documents. Pick an existing
          Matter to create a Subset in, or make a new Matter.
        </DialogDescription>

        {matterCards}
        <div className="mt-4 flex flex-col gap-2">
          {matters && matters.length > 0 ? (
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-sm text-gray-500">or</span>
              </div>
            </div>
          ) : (
            <div className="relative my-4">
              <p className="text-xs text-gray-700">
                It looks like you aren't a part of any matters yet. You need to create a matter, or be invited to one,
                before you can create a Subset.
              </p>
            </div>
          )}

          <Button variant="primary" className="w-full" onClick={handleCreateMatter}>
            Create new matter
            <Icon className="lucide--arrow-right h-4 w-4" />
          </Button>
        </div>

        <DialogFooter className="justify-center">
          <Button variant="outline" className="px-6" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SubsetCreationOnboarding = () => {
  const [isOpen, setIsOpen] = useState(true);

  const steps: StepItem[] = [
    {
      icon: <Icon className="lucide--pencil-line" />,
      title: 'Enter Subset details',
      description:
        'Add a name and your Subset issues. These issues will be used to determine the Relevancy of facts within the Subset.',
    },
    {
      icon: <Icon className="lucide--mouse-pointer" />,
      title: 'Select documents to be included in the Subset',
      description:
        'Select the documents you want to include in the Subset. You can always add or remove documents later.',
    },
    {
      icon: <Icon className="lucide--arrow-right" />,
      title: 'Create your Subset!',
      description:
        'Click Create Subset. You will be able to view the data in the Subset immediately, but it may take a few minutes for the fact Relevancy to be calculated.',
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-3xl" data-testid="onboarding-case-creation-modal">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--group" />
            Subset Creation
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="mb-4 text-sm text-gray-600">
          Subsets help you focus your analysis by only showing you facts from specific documents.
        </DialogDescription>

        <StepList steps={steps} />

        <DialogFooter className="justify-center">
          <Button variant="primary" className="px-6" onClick={() => setIsOpen(false)}>
            Got it!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
